import i18n from "i18next";
import {observable, action, computed, runInAction} from "mobx";
import {initReactI18next} from "react-i18next";
import IntlRelativeFormat from "intl-relativeformat";
import {FORMATS} from "../../common/utils/core/formats";
import moment from "moment-timezone";
import {TranslationsBase} from "../translations/base";
import {TranslationsEN} from "../translations/en";
import axios from "axios";
import {logger} from "../../common/utils/logger";

export interface IntlState {
  lng: string;
  locale: string;
  tz: string;
  currency: RestaurantSchema["settings"]["region"]["currency"] & { step: string };
  formats: RestaurantSchema["settings"]["region"]["formats"];
}

export interface IntlStoreOpts {
  initialState?: Partial<IntlState>;
  useReactModule: boolean;
}

export class IntlStore {

  @observable s: IntlState;

  i18n: i18n.i18n;
  rf?: IntlRelativeFormat;
  currencyFormatter?: Intl.NumberFormat;
  currencyFormatterUSD?: Intl.NumberFormat;
  numberFormatter?: Intl.NumberFormat;

  constructor(opts: IntlStoreOpts) {

    const isServer = typeof window === "undefined";

    const locale = (() => {
      if (isServer) return "en";
      if (navigator.languages) return navigator.languages[0] || "en";
      return navigator.language || "en";
    })();

    const lng = locale.split("-")[0];

    const tz = isServer ? "UTC" : Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.s = observable({
      lng: lng,
      locale: locale,
      tz: tz,
      currency: {
        symbol: "$",
        code: "USD",
        precision: 2,
        step: "0.01",
      },
      formats: {
        date: "DD/MM/YYYY",
        time: "h:mm a",
      },
      ...(opts.initialState || {}),
    });

    this.initFormatters(this.s);

    const base = opts.useReactModule ? i18n.use(initReactI18next) : i18n;

    base.init({
      lng: this.s.lng,
      fallbackLng: "en",
      interpolation: {
        escapeValue: false,
        format: this.format,
      },
      resources: {
        en: {
          translation: {
            ...TranslationsBase,
            ...TranslationsEN,
          },
        },
      },
    }).then();

    this.i18n = i18n;

  }

  initFormatters = (state: IntlState) => {

    if (state.tz) {

    }

    this.rf = new IntlRelativeFormat(state.locale);

    this.currencyFormatterUSD =  new Intl.NumberFormat(this.s.locale, {
      style: "currency",
      currency: "USD",
    });

    this.numberFormatter =  new Intl.NumberFormat(this.s.locale, {});

    this.currencyFormatter = new Intl.NumberFormat(state.locale, {
      style: "currency",
      currency: state.currency.code,
    });

    i18n.format = this.format;

  }

  format = (value: any, format: string | undefined) => {
    if (format === "currency") {
      const formatter = this.currencyFormatter;
      if (formatter)
        return formatter.format(value);
      return value;
    }
    if (format === "currencyUSD") {
      return this.currencyFormatterUSD!.format(value);
    }
    if (format === "number") {
      return this.numberFormatter!.format(value);
    }
    if (format === "timeFromTime") {
      return this.momentFromFormat(value, FORMATS.moment.time).format(this.formats.time);
    }
    if (format === "timeFromTimestamp") {
      return this.momentFromMillis(value).format(this.formats.time);
    }
    if (format === "dateFromDate") {
      return this.momentFromFormat(value, FORMATS.moment.date).format(this.formats.date);
    }
    if (format === "dateFromTimestamp") {
      return this.momentFromMillis(value).format(this.formats.date);
    }
    if (format === "dateFriendlyFromTimestamp") {
      return this.momentFromMillis(value).format(FORMATS.moment.date_friendly);
    }
    if (format === "datetimeFromTimestamp") {
      return this.momentFromMillis(value).format(this.formats.datetime);
    }
    if (format === "datetimeFromDateTime") {
      return this.momentFromFormat(value, FORMATS.moment.datetime).format(this.formats.datetime);
    }
    if (format === "relative") {
      const v = new Date(value);
      return this.rf ? this.rf.format(v) : new IntlRelativeFormat("en").format(v);
    }
    return value;
  }

  momentNow = () => {
    return moment.tz(this.s.tz).locale(this.s.lng);
  }

  momentFromFormat = (dt: string, format: string) => {
    return moment.tz(dt, format, this.s.tz).locale(this.s.lng);
  }

  momentFromMillis = (millis: number) => {
    return moment.tz(millis, this.s.tz).locale(this.s.lng);
  }

  @computed get formats() {
    const formats = this.s.formats;
    return {
      date: formats.date,
      time: formats.time,
      datetime: `${formats.date}, ${formats.time}`,
    };
  }

  changeLanguage = async (lng: string) => {

    try {

      if (!this.i18n.hasResourceBundle(lng, "translation")) {

        const apiUrl = process.env.NODE_ENV === "production" ?
          "https://api.cloudwaitress.com" : "http://localhost:3010";

        const res = await axios(`${apiUrl}/intl/translation?language=${lng}`);

        this.i18n.addResourceBundle(lng, "translation", res.data, true, true);

      }

      runInAction(() => {
        this.s.lng = lng;
        this.i18n.changeLanguage(lng);
      });

    }
    catch (e) {
      logger.captureException(e);
    }

  }

  @action set = (data: IntlState) => {
    this.s = data;
    this.initFormatters(this.s);
  }

  @action update = (data: Partial<IntlState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof IntlState];
        if (value !== undefined) {
          // @ts-ignore
          this.s[key as keyof IntlState] = value;
        }
      }
    }
    this.initFormatters(this.s);
  }

}
